import type { AreaMapBounds } from "@vendistri/ui";

const DEFAULT_MARKETPLACE_PATH = "/growth/marketplace";

export type MarketplaceBounds = AreaMapBounds;
type LocationPoint = { lat?: number | null; lng?: number | null };

/** Carry the visible operating area without sending location IDs or street addresses. */
export function marketplaceLocationsUrl(locations: LocationPoint[], viewport?: MarketplaceBounds | null): string {
  let bounds = viewport;
  if (!bounds) {
    const points = locations.filter((point): point is { lat: number; lng: number } =>
      typeof point.lat === "number" && typeof point.lng === "number"
      && Number.isFinite(point.lat) && Number.isFinite(point.lng)
      && Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180);
    if (points.length) {
      const latitudes = points.map(point => point.lat);
      const longitudes = points.map(point => point.lng);
      const south = Math.min(...latitudes);
      const north = Math.max(...latitudes);
      // Include opportunities roughly 25 miles beyond the current locations.
      const latitudePadding = 25 / 69;
      const longitudePadding = latitudePadding / Math.max(.01, Math.cos(Math.max(Math.abs(south), Math.abs(north)) * Math.PI / 180));
      bounds = { south: south - latitudePadding, north: north + latitudePadding,
        west: Math.min(...longitudes) - longitudePadding, east: Math.max(...longitudes) + longitudePadding };
    }
  }
  const search = new URLSearchParams({ view: "map" });
  if (bounds && Object.values(bounds).every(Number.isFinite)) {
    const area = {
      north: Math.min(90, Math.ceil(bounds.north * 10) / 10),
      south: Math.max(-90, Math.floor(bounds.south * 10) / 10),
      east: Math.min(180, Math.ceil(bounds.east * 10) / 10),
      west: Math.max(-180, Math.floor(bounds.west * 10) / 10),
    };
    if (area.south < area.north && area.west < area.east) {
      Object.entries(area).forEach(([key, value]) => search.set(key, String(value)));
    }
  }
  return `${DEFAULT_MARKETPLACE_PATH}?${search}`;
}

export function secureMarketplaceUrl(
  configuredUrl = process.env.REACT_APP_MARKETPLACE_URL,
): string {
  const value = configuredUrl?.trim();
  if (!value) return DEFAULT_MARKETPLACE_PATH;
  try {
    const url = new URL(value, window.location.origin);
    return url.origin === window.location.origin
      ? `${url.pathname.replace(/\/$/, "")}${url.search}${url.hash}`
      : url.toString();
  } catch {
    return DEFAULT_MARKETPLACE_PATH;
  }
}
